import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LocationDescriptor } from 'history';
import EditIcon from 'assets/edit.svg?react';
import { BlockingBottomSheet } from 'components/BlockingBottomSheet';
import { getReportEditPath } from 'features/reports/reports.utils';
import { useEditReportActionMenuState } from 'state/useEditReportActionMenuState';

const itemSx = { py: 1.5, px: 2, gap: 1.5 };

export const EditReportActionMenu = (): JSX.Element => {
  const { selectedReportForEdit, closeEditReportActionMenu: onClose } =
    useEditReportActionMenuState();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('phone'));
  const location = useLocation();

  const isOpen = selectedReportForEdit !== null;

  const editReportTo: LocationDescriptor | null = selectedReportForEdit && {
    pathname: getReportEditPath({
      geoEventType: selectedReportForEdit.geoEventType,
      geoEventId: selectedReportForEdit.report.geoEventId,
      reportId: selectedReportForEdit.report.id,
    }),
    state: { fromRoute: location.pathname + location.search },
  };

  const actionContent = (
    <>
      <ListItemIcon
        sx={{ '&.MuiListItemIcon-root': { minWidth: 'fit-content' } }}
      >
        <EditIcon />
      </ListItemIcon>
      <ListItemText
        primary={t('geoEvent.reports.editReport')}
        slotProps={{
          primary: { sx: { fontWeight: 600 } },
        }}
      />
    </>
  );

  if (isMobile) {
    return (
      <BlockingBottomSheet
        id="edit-report-bottom-sheet"
        open={isOpen}
        onDismiss={onClose}
        skipInitialTransition
        header
        aria-label={t('geoEvent.reports.reportActions')}
      >
        {editReportTo && (
          <ListItemButton
            component={Link}
            to={editReportTo}
            onClick={onClose}
            sx={itemSx}
          >
            {actionContent}
          </ListItemButton>
        )}
      </BlockingBottomSheet>
    );
  }

  return (
    <Menu
      anchorEl={selectedReportForEdit?.anchorEl ?? null}
      open={isOpen}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{
        list: {
          sx: {
            padding: 0,
            minWidth: 175,
          },
        },
      }}
    >
      {editReportTo && (
        <MenuItem
          component={Link}
          to={editReportTo}
          onClick={onClose}
          sx={itemSx}
        >
          {actionContent}
        </MenuItem>
      )}
    </Menu>
  );
};
